<template>
    <div v-if="reservation" class="lx-online-booking-quote-form">
        <b-container>
            <validation-observer ref="form" v-slot="{ handleSubmit, meta }">
                <b-form @submit.prevent="handleSubmit(_, sendMessage)">
                    <b-row>
                        <b-col md="12">
                            <card class="mb-4">
                                <template #header-content> Send Quote and Rental Information To: </template>
                                <template #body>
                                    <b-row>
                                        <b-col md="6">
                                            <validation-provider
                                                name="to_name"
                                                v-slot="{ errors, handleChange }"
                                                rules="required"
                                                
                                            >
                                                <form-input
                                                    v-model="quoteForm.to_name"
                                                    floating-label="Recipient's Name"
                                                    :leftIcon="['fas', 'user']"
                                                    :error="errors[0]"
                                                    @input="handleChange"
                                                />
                                            </validation-provider>
                                        </b-col>
                                        <b-col md="6">
                                            <validation-provider
                                                name="to_email"
                                                v-slot="{ errors, handleChange }"
                                                rules="required|email"
                                                
                                            >
                                                <form-input
                                                    v-model="quoteForm.to_email"
                                                    :leftIcon="['fas', 'at']"
                                                    floatingLabel="Recipient's Email"
                                                    :error="errors[0]"
                                                    @input="handleChange"
                                                ></form-input>
                                            </validation-provider>
                                        </b-col>
                                    </b-row>
                                    <b-row>
                                        <b-col md="6">
                                            <validation-provider
                                                name="from_name"
                                                v-slot="{ errors, handleChange }"
                                                rules="required"
                                                
                                            >
                                                <form-input
                                                    v-model="quoteForm.from_name"
                                                    floating-label="Your Name"
                                                    :leftIcon="['fas', 'user']"
                                                    :error="errors[0]"
                                                    @input="handleChange"
                                                />
                                            </validation-provider>
                                        </b-col>
                                        <b-col md="6">
                                            <validation-provider
                                                name="from_email"
                                                v-slot="{ errors, handleChange }"
                                                rules="required|email"
                                                
                                            >
                                                <form-input
                                                    v-model="quoteForm.from_email"
                                                    :leftIcon="['fas', 'at']"
                                                    floatingLabel="Your Email"
                                                    :error="errors[0]"
                                                    @input="handleChange"
                                                ></form-input>
                                            </validation-provider>
                                        </b-col>
                                    </b-row>
                                    <b-row>
                                        <b-col md="12">
                                            <validation-provider
                                                name="message"
                                                v-slot="{ errors, handleChange }"
                                                rules="maxMessageLenOrEqual:100,Message"
                                            >
                                                <form-input
                                                    v-model="quoteForm.message"
                                                    floating-label="Message"
                                                    :error="errors[0]"
                                                    @input="handleChange"
                                                ></form-input>
                                            </validation-provider>
                                        </b-col>
                                    </b-row>
                                    <b-row>
                                        <b-col cols="auto" class="mr-auto"></b-col>
                                        <b-col cols="auto" v-if="hasCaptcha">
                                            <RecaptchaV2 @load-callback="handleCaptcha" />
                                        </b-col>
                                        <b-col cols="auto">
                                            <b-button
                                                type="submit"
                                                :disabled="!meta.valid || (hasCaptcha && !quoteForm.token)"
                                                variant="primary"
                                                size="lg"
                                            >
                                                Send Message
                                            </b-button>
                                        </b-col>
                                    </b-row>
                                    <card class="mt-4" :nested="true">
                                        <template #header-content> Message Preview </template>
                                        <template #body>
                                            <b-row>
                                                <b-col
                                                    md="12"
                                                    class="lx-online-booking-quote-form__message-preview-col"
                                                >
                                                    <p class="lx-online-booking-quote-form__message-preview-message">
                                                        {{ quoteForm.message }}
                                                    </p>
                                                    <div v-html="onlineQuoteText"></div>
                                                </b-col>
                                            </b-row>
                                        </template>
                                    </card>
                                </template>
                            </card>
                        </b-col>
                    </b-row>
                </b-form>
            </validation-observer>
        </b-container>
    </div>
    <div v-else />
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../api';
import Card from '../../generic/card/card.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import { ONLINE_BOOKING_QUOTE_BY_ID_ENDPOINT } from '../../../endpoints';
import { RecaptchaV2 } from 'vue3-recaptcha-v2';

export default {
    name: 'OnlineBookingQuoteForm',
    components: { Card, FormInput, RecaptchaV2 },
    mounted() {
        (async () => {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                if (!this.reservation) {
                    await this.fetchReservation();
                } else if (this.reservation.is_online_quote) {
                    this.$router.push({ name: 'online-booking-quote-sent' });
                }
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        })();
    },
    data() {
        return {
            onlineQuoteHTML: '',
            quoteForm: {
                to_name: '',
                to_email: '',
                from_name: '',
                from_email: '',
                message: '',
                token: '',
            },
        };
    },
    computed: {
        ...mapGetters('onlineBooking', ['website', 'reservation', 'onlineQuoteText']),
        reservationID() {
            return this.$route.params.reservationId || 0;
        },
        hasCaptcha() {
            return this.website.has_captcha
        },
    },
    methods: {
        async fetchReservation() {
            const id = this.reservationID;
            try {
                await this.$store.dispatch('onlineBooking/fetchReservation', {
                    id: id,
                    withRentalAgreement: false,
                    withOnlineQuoteText: true,
                });
                if (this.reservation.is_online_quote) {
                    this.$router.push({ name: 'online-booking-quote-sent' });
                }
            } catch {
                await this.$router.push({ name: 'online-booking-calendar' });
                this.errorToast({
                    text: `Reservation #${id} has not been found.`,
                });
            }
        },
        async sendMessage() {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                const { data } = await API.post(ONLINE_BOOKING_QUOTE_BY_ID_ENDPOINT(this.website.url), this.quoteForm);
                this.$router.replace({
                    name: 'online-booking-quote-sent',
                    params: {
                        websiteUrl: this.website.url,
                        reservationId: data.id,
                    },
                });
            } catch (e) {
                this.errorToast({ text: 'Could not send message.' });
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        },
        handleCaptcha(e) {
            this.quoteForm.token = e;
        },
    },
};
</script>

<style lang="scss" scoped>
.lx-online-booking-quote-form {
    &__message-preview-col {
        text-align: -webkit-center;
    }

    &__message-preview-message {
        text-align: left;
        max-width: 562px;
    }

    :deep(.card-body) {
        padding-bottom: 0px !important;
    }
}
</style>
